import { useCallback } from "react";
import { Group, Button, Text, Stack, Card } from "@mantine/core";
import {
  IconDownload,
  IconDownloadOff,
  IconPrinter,
  IconPrinterOff,
} from "@tabler/icons-react";

interface PortalReportBulkActionsProps {
  selectedCount: number;
  onDownloadSelected: () => void;
  onDownloadFiltered: () => void;
  onPrintSelected: () => void;
  downloading: boolean;
  maxReports?: number;
}

export function PortalReportBulkActions({
  selectedCount,
  onDownloadSelected,
  onDownloadFiltered,
  onPrintSelected,
  downloading,
  maxReports = 50,
}: PortalReportBulkActionsProps) {
  const isBulkSelectedDisabled =
    selectedCount === 0 || selectedCount > maxReports || downloading;

  const handleDownloadSelectedClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      onDownloadSelected();
    },
    [onDownloadSelected]
  );

  const handleDownloadFilteredClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      onDownloadFiltered();
    },
    [onDownloadFiltered]
  );

  const handlePrintSelectedClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      onPrintSelected();
    },
    [onPrintSelected]
  );

  return (
    <Card
      withBorder
      padding="md"
      radius="md"
      bg="var(--mantine-color-gray-0)"
      style={{
        borderColor: "var(--mantine-color-gray-2)",
      }}
    >
      <Group justify="space-between" align="center" gap="md">
        <Stack gap={4}>
          <Text fw={600} size="sm">
            Bulk Actions
          </Text>
          <Text size="xs" c="dimmed">
            {selectedCount === 0
              ? "Select reports from the list below to perform bulk actions."
              : selectedCount > maxReports
              ? `You have selected ${selectedCount} reports. Select up to ${maxReports} reports to download them in bulk.`
              : `${selectedCount} report${
                  selectedCount === 1 ? "" : "s"
                } selected.`}
          </Text>
        </Stack>

        <Group
          gap="sm"
          wrap="nowrap"
          style={{ width: "100%", justifyContent: "flex-end" }}
          visibleFrom="sm"
        >
          <Button
            leftSection={
              selectedCount > maxReports ? (
                <IconPrinterOff size={16} />
              ) : (
                <IconPrinter size={16} />
              )
            }
            variant="filled"
            color="teal.6"
            onClick={handlePrintSelectedClick}
            disabled={isBulkSelectedDisabled}
            loading={downloading && selectedCount > 0}
          >
            Print Selected ({selectedCount})
          </Button>
          <Button
            leftSection={
              selectedCount > maxReports ? (
                <IconDownloadOff size={16} />
              ) : (
                <IconDownload size={16} />
              )
            }
            variant="outline"
            color="gcpBlue.6"
            onClick={handleDownloadSelectedClick}
            disabled={isBulkSelectedDisabled}
            loading={downloading && selectedCount > 0}
          >
            Download Selected ({selectedCount})
          </Button>
          <Button
            leftSection={<IconDownload size={16} />}
            variant="outline"
            color="gcpBlue.6"
            onClick={handleDownloadFilteredClick}
            disabled={downloading}
            loading={downloading && selectedCount === 0}
          >
            Download All Matching Filters
          </Button>
        </Group>

        {/* Stack for mobile, group for desktop */}
        <Stack gap="xs" style={{ width: "100%" }} hiddenFrom="sm">
          <Button
            leftSection={
              selectedCount > maxReports ? (
                <IconPrinterOff size={16} />
              ) : (
                <IconPrinter size={16} />
              )
            }
            variant="filled"
            color="teal.6"
            onClick={handlePrintSelectedClick}
            disabled={isBulkSelectedDisabled}
            loading={downloading && selectedCount > 0}
            fullWidth
          >
            Print Selected ({selectedCount})
          </Button>
          <Button
            leftSection={
              selectedCount > maxReports ? (
                <IconDownloadOff size={16} />
              ) : (
                <IconDownload size={16} />
              )
            }
            variant="outline"
            color="gcpBlue.6"
            onClick={handleDownloadSelectedClick}
            disabled={isBulkSelectedDisabled}
            loading={downloading && selectedCount > 0}
            fullWidth
          >
            Download Selected ({selectedCount})
          </Button>
          <Button
            leftSection={<IconDownload size={16} />}
            variant="outline"
            color="gcpBlue.6"
            onClick={handleDownloadFilteredClick}
            disabled={downloading}
            loading={downloading && selectedCount === 0}
            fullWidth
          >
            Download All Matching Filters
          </Button>
        </Stack>
      </Group>
    </Card>
  );
}

export default PortalReportBulkActions;
